/**
 * Admin -- Settings/General (Wave 1b W1b-01 wired)
 *
 * Sections:
 *   1. Tenant Identity -- name, slug (read-only), UUID, business unit, cost center, contacts
 *   2. Tenant Settings -- allow web search, allow external connections
 *   3. Deployment Mode -- IQ-Connected/Standalone/Air-Gapped (read-only display)
 *   4. Regional & Locale -- timezone, date format, business hours
 *   4. Environment Metadata -- env label, maintenance mode, debug mode, version
 *   5. Danger Zone -- a link to Data & Backup, which owns export and factory reset
 *
 * Mock data has been removed. First paint pulls from
 * /api/v1/admin/settings/general; mutations PATCH the same surface or
 * call the dedicated POST endpoints (/maintenance-mode, /read-only-mode,
 * /debug-mode, /rename-slug). Every mutation emits an audit_chain row
 * server-side.
 *
 * USR-F1-T4:
 *   - THE SCREEN ASKS THE ROUTE'S KEY (AC-07; decision 26). Every write control
 *     is drawn only when the server-reported grant set holds the key its route
 *     declares: `write:settings` for the two Save buttons, the two switches,
 *     Establish and the three mode toggles; `write:tenants` for Request
 *     rename (held by no tenant role today — founder question Q1). Without
 *     the key every field is read-only with a reason and the controls are not
 *     in the DOM. A Viewer and a Member still READ the page (decision 11).
 *   - ONE TENANT NAME (AC-08; decision 20). The Tenant name input shows
 *     organizations.name (`identity.name`) — the string the breadcrumb and
 *     the tenant picker show — and saves through PATCH /organizations/:id,
 *     its one writer (audited tenant.metadata.changed). That store has no
 *     changed_by, so the field draws no chip; Admin › Audit is its history.
 *   - NAMED, NEVER AN ID (AC-05/06). `changedMeta[k].changedBy` is the person
 *     the server named (NamedUser) or null; the chip shows `.name`.
 *   - NO FAKE AFFORDANCE (AC-09). The Danger Zone links to Data & Backup;
 *     the stub factory-reset dialog and the "navigate" toast are gone.
 */

import { createFileRoute, Link } from '@tanstack/react-router';
import { useState, useEffect, useCallback } from 'react';
import {
  Building2, Server, Globe, Activity, Copy, AlertTriangle,
  User as UserIcon, Mail, Phone, Loader2, Save,
} from 'lucide-react';
import { Input } from '@/components/ui/input';
import { Button, buttonVariants } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Switch } from '@/components/ui/switch';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select';
import { cn } from '@/lib/utils';
import { toast } from 'sonner';
import {
  SettingField, SectionCard, DangerZone, DangerAction, StatusDot,
} from '@/components/admin';
import { TypedConfirmDialog } from '@/components/admin/typed-confirm-dialog';
import { apiClient } from '@/lib/api-client';
import { useHasPermission } from '@/lib/use-has-permission';
import type { NamedUser } from '@argusai/shared/schemas/user-display';

/** Why a field is read-only for someone without `write:settings`. */
const NO_WRITE_REASON = 'Changing this needs the write:settings permission (Admin or Owner).';

export const Route = createFileRoute('/admin/settings/')({
  component: GeneralPage,
});

interface GeneralResponse {
  identity: {
    id: string;
    /** THE tenant name: organizations.name (AC-08). */
    name: string;
    slug: string;
    orgCode: string;
    isRoot: boolean;
    createdAt: string;
    businessUnit: string | null;
    costCenter: string | null;
  };
  contacts: {
    primary: { name: string; email: string; phone: string | null } | null;
    technical: { name: string; email: string; phone: string | null } | null;
    compliance: { name: string; email: string; phone: string | null } | null;
  };
  deploymentMode: {
    mode: 'iq_connected' | 'standalone' | 'air_gapped';
    health: { status: 'healthy' | 'degraded' | 'unknown'; detail: string | null };
  };
  locale: Record<string, unknown>;
  /**
   * The two tenant settings (Foundations 1 / B0-T1). `null` when the server
   * could not read them — the page says so rather than rendering two `off`
   * switches that look like a deliberate choice.
   */
  policy: {
    allowWebSearch: boolean;
    allowExternalConnections: boolean;
  } | null;
  ops: {
    maintenanceMode: boolean;
    readOnlyMode: boolean;
    debugMode: boolean;
    debugModeUntil: string | null;
  };
  environment: {
    environment: string;
    version: string;
    buildId: string;
  };
  changedMeta: Record<
    string,
    // The person the server named, or null — never an id (AC-05).
    { changedBy: NamedUser | null; changedAt: string; changeReason: string | null }
  >;
}

function GeneralPage() {
  const [data, setData] = useState<GeneralResponse | null>(null);
  const [loading, setLoading] = useState(true);

  // The keys the routes declare (AC-07): write:settings guards every write on
  // this page; write:tenants guards the slug rename (nobody holds it — Q1).
  const canWrite = useHasPermission('write', 'settings');
  const canRenameSlug = useHasPermission('write', 'tenants');
  // The Danger Zone's only control opens Data & Backup; it is drawn with the
  // same key that tab is drawn with (settings.tsx SUB_TABS; decision 26).
  const canReadData = useHasPermission('read', 'settings.data');

  // Editable identity fields (initialised from server payload).
  const [orgName, setOrgName] = useState('');
  const [businessUnit, setBusinessUnit] = useState('');
  const [costCenter, setCostCenter] = useState('');

  // Locale fields
  const [defaultLocale, setDefaultLocale] = useState('en-US');
  const [defaultTimezone, setDefaultTimezone] = useState('America/New_York');
  const [dateFormat, setDateFormat] = useState('MMM d, yyyy');
  const [timeFormat, setTimeFormat] = useState<'12h' | '24h'>('24h');
  const [currency, setCurrency] = useState('USD');
  const [fiscalYearStart, setFiscalYearStart] = useState('01-01');
  const [businessHoursStart, setBusinessHoursStart] = useState('09:00');
  const [businessHoursEnd, setBusinessHoursEnd] = useState('17:00');

  // Saving / dialog state
  const [savingIdentity, setSavingIdentity] = useState(false);
  const [savingLocale, setSavingLocale] = useState(false);
  const [savingPolicy, setSavingPolicy] = useState(false);
  const [renameOpen, setRenameOpen] = useState(false);
  const [maintenanceModePendingEnable, setMaintenanceModePendingEnable] =
    useState(false);
  const [readOnlyModePendingEnable, setReadOnlyModePendingEnable] =
    useState(false);

  const refresh = useCallback(async () => {
    try {
      setLoading(true);
      const r = await apiClient.get<GeneralResponse>('/admin/settings/general/');
      setData(r);
      setOrgName(r.identity.name);
      setBusinessUnit(r.identity.businessUnit ?? '');
      setCostCenter(r.identity.costCenter ?? '');
      const loc = r.locale ?? {};
      setDefaultLocale((loc.default_locale as string | null) ?? 'en-US');
      setDefaultTimezone(
        (loc.default_timezone as string | null) ?? 'America/New_York'
      );
      setDateFormat((loc.date_format as string | null) ?? 'MMM d, yyyy');
      setTimeFormat((loc.time_format as '12h' | '24h' | null) ?? '24h');
      setCurrency((loc.currency as string | null) ?? 'USD');
      setFiscalYearStart((loc.fiscal_year_start as string | null) ?? '01-01');
      setBusinessHoursStart(
        (loc.business_hours_start as string | null) ?? '09:00'
      );
      setBusinessHoursEnd(
        (loc.business_hours_end as string | null) ?? '17:00'
      );
    } catch (err) {
      const msg = err instanceof Error ? err.message : 'Failed to load settings';
      toast.error(msg);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    refresh();
  }, [refresh]);

  async function saveIdentity() {
    if (!data) return;
    const name = orgName.trim();
    if (name.length === 0) {
      toast.error('The tenant name cannot be empty');
      return;
    }
    // Two writes, two stores: the rename can land and the second write still
    // fail. Track it so the page never reports a landed rename as unsaved.
    let renamed = false;
    try {
      setSavingIdentity(true);
      // The tenant's name has ONE writer: PATCH /organizations/:id (decision
      // 20) — the same string the breadcrumb and the tenant picker show.
      if (name !== data.identity.name) {
        await apiClient.patch(`/organizations/${data.identity.id}`, { name });
        renamed = true;
        // The rename is saved: hold it as the server's value now, so a retry
        // after a failed second write does not rename (and audit) again.
        setData((d) => (d ? { ...d, identity: { ...d.identity, name } } : d));
      }
      await apiClient.patch('/admin/settings/general/', {
        businessUnit: businessUnit.trim() || null,
        costCenter: costCenter.trim() || null,
        reason: 'Identity update from admin UI',
      });
      toast.success('Tenant identity updated');
      await refresh();
    } catch (err) {
      const msg = err instanceof Error ? err.message : 'Save failed';
      // The business unit / cost center the admin typed stay in the form for
      // the retry; only the landed rename is reported as landed.
      toast.error(
        renamed
          ? `The tenant was renamed, but business unit and cost center were not saved: ${msg}`
          : msg
      );
    } finally {
      setSavingIdentity(false);
    }
  }

  // The two tenant settings. Each flip is its own audited PATCH — there is no
  // "save" button for a switch, and the server is the source of truth for the
  // new value (we refresh rather than optimistically flipping local state, so
  // a refused write cannot leave the UI claiming a capability the gate denies).
  async function savePolicy(
    key: 'allowWebSearch' | 'allowExternalConnections',
    value: boolean
  ) {
    try {
      setSavingPolicy(true);
      await apiClient.patch('/admin/settings/general/', {
        [key]: value,
        reason: 'Tenant settings update from admin UI',
      });
      toast.success(
        key === 'allowWebSearch'
          ? `Web search ${value ? 'allowed' : 'turned off'}`
          : `External connections ${value ? 'allowed' : 'turned off'}`
      );
      await refresh();
    } catch (err) {
      const msg = err instanceof Error ? err.message : 'Save failed';
      toast.error(msg);
      // Re-read so the switch snaps back to whatever the server actually holds.
      await refresh();
    } finally {
      setSavingPolicy(false);
    }
  }

  /**
   * Establish the two settings when the page could not read them.
   *
   * Review BF-06: `resolveTenantSettings` fails closed on a MISSING row, and
   * the page renders the outage banner INSTEAD of the switches — so the write
   * that would create the rows was unreachable from the UI and only a direct
   * DB insert fixed it. This sends both keys in one audited PATCH (the same
   * endpoint the switches use, `setTenantSetting` upserts), writing the safe
   * posture: both off. The admin then turns on what they want, deliberately.
   */
  async function establishPolicy() {
    try {
      setSavingPolicy(true);
      await apiClient.patch('/admin/settings/general/', {
        allowWebSearch: false,
        allowExternalConnections: false,
        reason: 'Establish tenant policy settings from admin UI',
      });
      toast.success('Tenant settings established — both are off until you allow them');
      await refresh();
    } catch (err) {
      const msg = err instanceof Error ? err.message : 'Could not establish the settings';
      toast.error(msg);
      await refresh();
    } finally {
      setSavingPolicy(false);
    }
  }

  async function saveLocale() {
    try {
      setSavingLocale(true);
      await apiClient.patch('/admin/settings/general/', {
        locale: {
          default_locale: defaultLocale,
          default_timezone: defaultTimezone,
          date_format: dateFormat,
          time_format: timeFormat,
          currency,
          fiscal_year_start: fiscalYearStart,
          business_hours_start: businessHoursStart,
          business_hours_end: businessHoursEnd,
        },
        reason: 'Locale update from admin UI',
      });
      toast.success('Locale settings updated');
      await refresh();
    } catch (err) {
      const msg = err instanceof Error ? err.message : 'Save failed';
      toast.error(msg);
    } finally {
      setSavingLocale(false);
    }
  }

  async function setMode(
    field: 'maintenance-mode' | 'read-only-mode' | 'debug-mode',
    enabled: boolean,
    reason: string,
    extra?: Record<string, unknown>
  ) {
    try {
      await apiClient.post(`/admin/settings/general/${field}`, {
        enabled,
        reason,
        ...(extra ?? {}),
      });
      toast.success(
        `${field === 'debug-mode' ? 'Debug' : field === 'maintenance-mode' ? 'Maintenance' : 'Read-only'} mode ${enabled ? 'enabled' : 'disabled'}`
      );
      await refresh();
    } catch (err) {
      const msg = err instanceof Error ? err.message : 'Toggle failed';
      toast.error(msg);
    }
  }

  if (loading || !data) {
    return (
      <div className="max-w-4xl mx-auto p-6">
        <div className="flex items-center gap-2 text-xs text-muted-foreground">
          <Loader2 className="h-3.5 w-3.5 animate-spin" />
          Loading settings...
        </div>
      </div>
    );
  }

  const buMeta = data.changedMeta['business_unit'];
  const ccMeta = data.changedMeta['cost_center'];

  return (
    <div className="max-w-4xl mx-auto p-6 space-y-5">
      {/* Page header */}
      <div>
        <h1 className="text-xl font-semibold">General</h1>
        <p className="text-xs text-muted-foreground mt-1">
          Tenant identity, deployment mode, locale, and environment metadata.
        </p>
      </div>

      {/* 1. Tenant Identity */}
      <SectionCard
        title="Tenant Identity"
        description="Information about this tenant. Slug and UUID are permanent."
        icon={Building2}
        actions={
          canWrite && (
          <Button
            size="sm"
            className="h-8 gap-1.5"
            onClick={saveIdentity}
            disabled={savingIdentity}
            title="Save identity changes"
          >
            {savingIdentity ? (
              <Loader2 className="h-3.5 w-3.5 animate-spin" />
            ) : (
              <Save className="h-3.5 w-3.5" />
            )}
            Save changes
          </Button>
          )
        }
      >
        <div className="grid grid-cols-2 gap-4">
          {/*
           * Bug #992 — Playwright probes locate identity inputs by
           * data-testid (`tenant-name-input`, etc). Without the testid
           * the cycle-3.5 deep-pass probe couldn't see the field even
           * though the page rendered correctly. Mirrors the testid
           * pattern already used by other admin forms.
           */}
          {/* organizations.name has no changed_by: no chip here — the rename
              is audited as tenant.metadata.changed (Admin › Audit). */}
          <SettingField
            label="Tenant name"
            description="Display name shown throughout the product."
            readOnly={!canWrite}
            readOnlyReason={NO_WRITE_REASON}
          >
            <Input
              data-testid="tenant-name-input"
              value={orgName}
              onChange={(e) => setOrgName(e.target.value)}
              readOnly={!canWrite}
              className="h-9 text-xs"
              name="name"
              aria-label="Tenant name"
            />
          </SettingField>

          <SettingField
            label="Tenant slug"
            description="URL-safe identifier. Cannot be changed without 2-person approval."
            readOnly
            readOnlyReason="Requires 2-person approval to rename"
          >
            <div className="flex items-center gap-2">
              <Input
                data-testid="tenant-slug-input"
                value={data.identity.slug}
                disabled
                className="h-9 text-xs font-mono"
              />
              {canRenameSlug && (
              <Button
                size="sm"
                variant="outline"
                className="shrink-0 h-9"
                onClick={() => setRenameOpen(true)}
                title="Request a slug rename (2-person approval required)"
              >
                Request rename
              </Button>
              )}
            </div>
          </SettingField>

          <SettingField
            label="Tenant UUID"
            description="Internal unique identifier. Referenced in API calls and audit logs."
            readOnly
            readOnlyReason="Permanent identifier"
          >
            <div className="flex items-center gap-2">
              <Input
                data-testid="tenant-uuid-input"
                value={data.identity.id}
                disabled
                className="h-9 text-xs font-mono"
              />
              <Button
                size="icon"
                variant="ghost"
                className="h-9 w-9 shrink-0"
                // Issue #997 — stable aria-label + data-testid for the
                // icon-only copy button (title alone leaves SR/Playwright
                // without selectors).
                title="Copy tenant UUID"
                aria-label="Copy tenant UUID"
                data-testid="copy-tenant-uuid"
                onClick={() => {
                  navigator.clipboard.writeText(data.identity.id);
                  toast.success('UUID copied');
                }}
              >
                <Copy className="h-3.5 w-3.5" />
              </Button>
            </div>
          </SettingField>

          <SettingField
            label="Business unit"
            description="For chargeback and reporting."
            changedBy={buMeta?.changedBy?.name}
            changedAt={buMeta?.changedAt}
            readOnly={!canWrite}
            readOnlyReason={NO_WRITE_REASON}
          >
            <Input
              data-testid="tenant-business-unit-input"
              value={businessUnit}
              onChange={(e) => setBusinessUnit(e.target.value)}
              readOnly={!canWrite}
              className="h-9 text-xs"
            />
          </SettingField>

          <SettingField
            label="Cost center"
            changedBy={ccMeta?.changedBy?.name}
            changedAt={ccMeta?.changedAt}
            readOnly={!canWrite}
            readOnlyReason={NO_WRITE_REASON}
          >
            <Input
              data-testid="tenant-cost-center-input"
              value={costCenter}
              onChange={(e) => setCostCenter(e.target.value)}
              readOnly={!canWrite}
              className="h-9 text-xs"
            />
          </SettingField>

        </div>

        <div className="border-t pt-4 mt-2">
          <p className="text-[10px] uppercase tracking-wider font-semibold text-muted-foreground mb-3">
            Contacts
          </p>
          <div className="space-y-3">
            <ContactRow
              label="Primary contact"
              icon={UserIcon}
              contact={data.contacts.primary}
            />
            <ContactRow
              label="Technical contact"
              icon={UserIcon}
              contact={data.contacts.technical}
            />
            <ContactRow
              label="Compliance officer"
              icon={UserIcon}
              contact={data.contacts.compliance}
            />
          </div>
        </div>
      </SectionCard>

      {/* 2. Tenant Settings — the only two per-tenant policy switches.
          Foundations 1 / B0-T1 (decisions.md 2026-09-08, "Features, flags and
          plans"): the Features tab and its 22 toggles are gone; every rail is
          simply on. These two decide whether this tenant may reach outside,
          and they are the tenant admin's, not the sysadmin's. */}
      <SectionCard
        title="Tenant Settings"
        description="Whether this tenant may reach outside. Every rail and every capability is on."
        icon={Globe}
      >
        {data.policy === null ? (
          <div
            className="flex items-start gap-2 text-xs text-amber-700 dark:text-amber-300 bg-amber-500/10 border border-amber-500/30 rounded px-3 py-2"
            data-testid="tenant-policy-unreadable"
          >
            <AlertTriangle className="h-3.5 w-3.5 mt-0.5 shrink-0" />
            <div className="space-y-2">
              <div>
                These settings could not be read. Until that is fixed both
                capabilities are refused — this is an outage, not a choice you made.
              </div>
              {/* The recovery path (review BF-06). The commonest cause is a
                  tenant created by a path that never established the two rows;
                  the write endpoint UPSERTS, so establishing them is a normal
                  audited PATCH — it was simply unreachable while the banner
                  replaced the switches. Writes the safe posture (both off) and
                  re-reads; if the store itself is down the PATCH fails and the
                  banner stays, which is the honest answer. */}
              {canWrite ? (
              <Button
                size="sm"
                variant="outline"
                className="h-7 text-xs"
                data-testid="establish-tenant-policy"
                disabled={savingPolicy}
                onClick={establishPolicy}
              >
                Establish these settings
              </Button>
              ) : (
                <div>{NO_WRITE_REASON}</div>
              )}
            </div>
          </div>
        ) : (
          <div className="space-y-3">
            <SettingField
              label="Allow web search"
              description="Let people turn on live web search per turn in the chat composer. Results are marked as an unverified Web source."
              changedBy={data.changedMeta['policy.allow_web_search']?.changedBy?.name}
              changedAt={data.changedMeta['policy.allow_web_search']?.changedAt}
              readOnly={!canWrite}
              readOnlyReason={NO_WRITE_REASON}
            >
              {canWrite ? (
              <Switch
                data-testid="setting-allow-web-search"
                checked={data.policy.allowWebSearch}
                disabled={savingPolicy}
                onCheckedChange={(v) => savePolicy('allowWebSearch', v)}
              />
              ) : (
                <ReadOnlyState on={data.policy.allowWebSearch} />
              )}
            </SettingField>

            <SettingField
              label="Allow external connections"
              description="Let this tenant connect out to outside systems — connectors and MCP servers. Off means fully inward-facing."
              changedBy={
                data.changedMeta['policy.allow_external_connections']?.changedBy?.name
              }
              changedAt={
                data.changedMeta['policy.allow_external_connections']?.changedAt
              }
              readOnly={!canWrite}
              readOnlyReason={NO_WRITE_REASON}
            >
              {canWrite ? (
              <Switch
                data-testid="setting-allow-external-connections"
                checked={data.policy.allowExternalConnections}
                disabled={savingPolicy}
                onCheckedChange={(v) => savePolicy('allowExternalConnections', v)}
              />
              ) : (
                <ReadOnlyState on={data.policy.allowExternalConnections} />
              )}
            </SettingField>
          </div>
        )}
      </SectionCard>

      {/* 3. Deployment Mode */}
      <SectionCard
        title="Deployment Mode"
        description="How this tenant is deployed. Changing modes requires sysadmin coordination."
        icon={Server}
      >
        <div className="flex items-center gap-3 rounded-md border bg-muted/20 p-3">
          <div
            className={cn(
              'h-10 w-10 rounded-md flex items-center justify-center shrink-0',
              data.deploymentMode.mode === 'air_gapped' && 'bg-emerald-500/10',
              data.deploymentMode.mode === 'iq_connected' && 'bg-blue-500/10',
              data.deploymentMode.mode === 'standalone' && 'bg-violet-500/10'
            )}
          >
            <Server
              className={cn(
                'h-5 w-5',
                data.deploymentMode.mode === 'air_gapped' &&
                  'text-emerald-600 dark:text-emerald-400',
                data.deploymentMode.mode === 'iq_connected' &&
                  'text-blue-600 dark:text-blue-400',
                data.deploymentMode.mode === 'standalone' &&
                  'text-violet-600 dark:text-violet-400'
              )}
            />
          </div>
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 flex-wrap">
              <span className="text-sm font-semibold">
                {data.deploymentMode.mode === 'iq_connected' && 'IQ-Connected'}
                {data.deploymentMode.mode === 'standalone' && 'Standalone'}
                {data.deploymentMode.mode === 'air_gapped' && 'Air-Gapped'}
              </span>
              <StatusDot
                status={
                  data.deploymentMode.health.status === 'healthy'
                    ? 'healthy'
                    : 'degraded'
                }
              />
              <span className="text-[11px] text-muted-foreground capitalize">
                {data.deploymentMode.health.status}
              </span>
            </div>
            <p className="text-[11px] text-muted-foreground mt-0.5">
              {data.deploymentMode.mode === 'iq_connected' &&
                'Validates ArgusIQ-issued JWTs. Data flows from IQ via MCP.'}
              {data.deploymentMode.mode === 'standalone' &&
                'Issues own JWTs. Full user management + SSO.'}
              {data.deploymentMode.mode === 'air_gapped' &&
                'No external network calls. Fully self-contained on premise.'}
            </p>
          </div>
        </div>
      </SectionCard>

      {/* 4. Regional & Locale */}
      <SectionCard
        title="Regional & Locale"
        description="Default timezone, date format, and business hours."
        icon={Globe}
        actions={
          canWrite && (
          <Button
            size="sm"
            className="h-8 gap-1.5"
            onClick={saveLocale}
            disabled={savingLocale}
            title="Save locale changes"
          >
            {savingLocale ? (
              <Loader2 className="h-3.5 w-3.5 animate-spin" />
            ) : (
              <Save className="h-3.5 w-3.5" />
            )}
            Save changes
          </Button>
          )
        }
      >
        <div className="grid grid-cols-2 gap-4">
          <SettingField label="Default locale" readOnly={!canWrite} readOnlyReason={NO_WRITE_REASON}>
            <Select value={defaultLocale} onValueChange={setDefaultLocale} disabled={!canWrite}>
              <SelectTrigger aria-label="Default locale" className="h-9 text-xs"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="en-US" className="text-xs">English (United States)</SelectItem>
                <SelectItem value="en-GB" className="text-xs">English (United Kingdom)</SelectItem>
                <SelectItem value="es-ES" className="text-xs">Spanish (Spain)</SelectItem>
                <SelectItem value="de-DE" className="text-xs">German (Germany)</SelectItem>
              </SelectContent>
            </Select>
          </SettingField>

          <SettingField label="Default timezone" readOnly={!canWrite} readOnlyReason={NO_WRITE_REASON}>
            <Select value={defaultTimezone} onValueChange={setDefaultTimezone} disabled={!canWrite}>
              <SelectTrigger aria-label="Default timezone" className="h-9 text-xs"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="America/New_York"    className="text-xs">America/New_York (ET)</SelectItem>
                <SelectItem value="America/Chicago"     className="text-xs">America/Chicago (CT)</SelectItem>
                <SelectItem value="America/Denver"      className="text-xs">America/Denver (MT)</SelectItem>
                <SelectItem value="America/Los_Angeles" className="text-xs">America/Los_Angeles (PT)</SelectItem>
                <SelectItem value="Europe/London"       className="text-xs">Europe/London</SelectItem>
                <SelectItem value="Europe/Berlin"       className="text-xs">Europe/Berlin</SelectItem>
                <SelectItem value="Asia/Tokyo"          className="text-xs">Asia/Tokyo</SelectItem>
              </SelectContent>
            </Select>
          </SettingField>

          <SettingField label="Date format" readOnly={!canWrite} readOnlyReason={NO_WRITE_REASON}>
            <Select value={dateFormat} onValueChange={setDateFormat} disabled={!canWrite}>
              <SelectTrigger aria-label="Date format" className="h-9 text-xs"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="MMM d, yyyy"   className="text-xs">Apr 20, 2026</SelectItem>
                <SelectItem value="yyyy-MM-dd"    className="text-xs">2026-04-20</SelectItem>
                <SelectItem value="d MMM yyyy"    className="text-xs">20 Apr 2026</SelectItem>
                <SelectItem value="MM/dd/yyyy"    className="text-xs">04/20/2026</SelectItem>
                <SelectItem value="dd/MM/yyyy"    className="text-xs">20/04/2026</SelectItem>
              </SelectContent>
            </Select>
          </SettingField>

          <SettingField label="Time format" readOnly={!canWrite} readOnlyReason={NO_WRITE_REASON}>
            <Select value={timeFormat} onValueChange={(v) => setTimeFormat(v as '12h' | '24h')} disabled={!canWrite}>
              <SelectTrigger aria-label="Time format" className="h-9 text-xs"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="24h" className="text-xs">24-hour (17:30)</SelectItem>
                <SelectItem value="12h" className="text-xs">12-hour (5:30 PM)</SelectItem>
              </SelectContent>
            </Select>
          </SettingField>

          <SettingField label="Currency" readOnly={!canWrite} readOnlyReason={NO_WRITE_REASON}>
            <Select value={currency} onValueChange={setCurrency} disabled={!canWrite}>
              <SelectTrigger aria-label="Currency" className="h-9 text-xs"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="USD" className="text-xs">USD - US Dollar</SelectItem>
                <SelectItem value="EUR" className="text-xs">EUR - Euro</SelectItem>
                <SelectItem value="GBP" className="text-xs">GBP - British Pound</SelectItem>
                <SelectItem value="JPY" className="text-xs">JPY - Japanese Yen</SelectItem>
              </SelectContent>
            </Select>
          </SettingField>

          <SettingField label="Fiscal year start" readOnly={!canWrite} readOnlyReason={NO_WRITE_REASON}>
            <Input
              value={fiscalYearStart}
              onChange={(e) => setFiscalYearStart(e.target.value)}
              readOnly={!canWrite}
              placeholder="MM-DD"
              className="h-9 text-xs font-mono"
            />
          </SettingField>
        </div>

        <div className="border-t pt-4 mt-2">
          <p className="text-[10px] uppercase tracking-wider font-semibold text-muted-foreground mb-3">
            Business hours
          </p>
          <div className="grid grid-cols-2 gap-4">
            <SettingField label="Start" readOnly={!canWrite} readOnlyReason={NO_WRITE_REASON}>
              <Input
                value={businessHoursStart}
                onChange={(e) => setBusinessHoursStart(e.target.value)}
                readOnly={!canWrite}
                type="time"
                className="h-9 text-xs font-mono"
              />
            </SettingField>
            <SettingField label="End" readOnly={!canWrite} readOnlyReason={NO_WRITE_REASON}>
              <Input
                value={businessHoursEnd}
                onChange={(e) => setBusinessHoursEnd(e.target.value)}
                readOnly={!canWrite}
                type="time"
                className="h-9 text-xs font-mono"
              />
            </SettingField>
          </div>
          <p className="text-[10px] text-muted-foreground mt-2">
            Used for notification quiet-hour defaults and scheduled agent runs.
          </p>
        </div>
      </SectionCard>

      {/* 4. Environment Metadata */}
      <SectionCard
        title="Environment"
        description="Operational state and build metadata."
        icon={Activity}
      >
        <div className="grid grid-cols-2 gap-3 mb-3">
          <Fact
            label="Environment"
            value={
              <Badge
                variant="outline"
                className="text-[10px] bg-emerald-500/10 text-emerald-700 dark:text-emerald-300 border-emerald-500/30"
              >
                {data.environment.environment}
              </Badge>
            }
          />
          <Fact
            label="Version"
            value={<code className="text-[11px]">{data.environment.version}</code>}
          />
          <Fact
            label="Build"
            value={<code className="text-[11px]">{data.environment.buildId}</code>}
          />
          <Fact
            label="Tenant created"
            value={
              <span className="tabular-nums text-[11px]">
                {new Date(data.identity.createdAt).toLocaleDateString()}
              </span>
            }
          />
        </div>

        <div className="border-t pt-4 space-y-3">
          <p className="text-[10px] uppercase tracking-wider font-semibold text-muted-foreground">
            Operational modes
          </p>

          <ModeToggle
            label="Maintenance mode"
            description="Blocks all non-admin traffic. Admin banner shown. Use during migrations."
            enabled={data.ops.maintenanceMode}
            canChange={canWrite}
            onChange={(v) => {
              if (v) {
                setMaintenanceModePendingEnable(true);
              } else {
                setMode(
                  'maintenance-mode',
                  false,
                  'Disabled from admin UI'
                );
              }
            }}
            destructive
          />

          <ModeToggle
            label="Read-only mode"
            description="Disables all write operations. Useful during backup restore or DR testing."
            enabled={data.ops.readOnlyMode}
            canChange={canWrite}
            onChange={(v) => {
              if (v) {
                setReadOnlyModePendingEnable(true);
              } else {
                setMode('read-only-mode', false, 'Disabled from admin UI');
              }
            }}
            destructive
          />

          <ModeToggle
            label="Debug mode"
            description={
              data.ops.debugModeUntil
                ? `Auto-disables at ${new Date(data.ops.debugModeUntil).toLocaleTimeString()}`
                : 'Enables verbose logging. Auto-disables after 1-24 hours.'
            }
            enabled={data.ops.debugMode}
            canChange={canWrite}
            onChange={(v) => {
              if (v) {
                setMode('debug-mode', true, 'Enabled from admin UI', {
                  ttlHours: 4,
                });
              } else {
                setMode('debug-mode', false, 'Disabled from admin UI');
              }
            }}
          />
        </div>
      </SectionCard>

      {/* Danger Zone */}
      {/* AC-09 — no control here calls no route. Data & Backup owns tenant
          export and factory reset (data-backup.ts, factory:reset:settings.data);
          this is the way there. */}
      {canReadData && (
      <DangerZone>
        <DangerAction
          title="Export or factory reset this tenant"
          description="Full tenant exports, legal holds and factory reset live on the Data & Backup tab."
          action={
            <Link
              to="/admin/settings/data"
              title="Open the Data & Backup tab"
              className={buttonVariants({ variant: 'outline', size: 'sm' })}
            >
              Open Data & Backup
            </Link>
          }
        />
      </DangerZone>
      )}

      {/* Dialogs */}
      {canRenameSlug && (
      <SlugRenameDialog
        open={renameOpen}
        onOpenChange={setRenameOpen}
        currentSlug={data.identity.slug}
        onRenamed={async () => {
          setRenameOpen(false);
          await refresh();
        }}
      />
      )}

      <TypedConfirmDialog
        open={maintenanceModePendingEnable}
        onOpenChange={setMaintenanceModePendingEnable}
        title="Enter maintenance mode"
        description="Maintenance mode blocks all non-admin traffic. Users see a maintenance banner. Only use when you actively need to block tenant access."
        consequences={[
          'All non-admin traffic returns 503 Service Unavailable',
          'Maintenance banner is shown to admin users',
          'Background jobs continue running',
          'Can be disabled at any time to restore access',
        ]}
        confirmPhrase="enter maintenance"
        confirmLabel="Enter maintenance"
        requireReason
        reasonPlaceholder="e.g. DB migration - ticket OPS-4821"
        variant="warning"
        onConfirm={async () => {
          setMaintenanceModePendingEnable(false);
          await setMode(
            'maintenance-mode',
            true,
            'Enabled from admin UI - typed-confirm acknowledged'
          );
        }}
      />

      <TypedConfirmDialog
        open={readOnlyModePendingEnable}
        onOpenChange={setReadOnlyModePendingEnable}
        title="Enter read-only mode"
        description="Read-only mode disables all write operations. Reads continue to work. Use during backup restore or DR testing."
        consequences={[
          'All writes return 405 Method Not Allowed',
          'Reads continue normally',
          'Queued writes are held - they do not fail',
          'Can be disabled at any time to resume writes',
        ]}
        confirmPhrase="enter read-only"
        confirmLabel="Enter read-only"
        requireReason
        reasonPlaceholder="e.g. Restoring from backup - ticket DR-0042"
        variant="warning"
        onConfirm={async () => {
          setReadOnlyModePendingEnable(false);
          await setMode(
            'read-only-mode',
            true,
            'Enabled from admin UI - typed-confirm acknowledged'
          );
        }}
      />
    </div>
  );
}

// ─── Slug rename dialog ─────────────────────────────────────────────────────

import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from '@/components/ui/dialog';
import { Label } from '@/components/ui/label';

function SlugRenameDialog({
  open, onOpenChange, currentSlug, onRenamed,
}: {
  open: boolean;
  onOpenChange: (v: boolean) => void;
  currentSlug: string;
  onRenamed: () => Promise<void>;
}) {
  const [newSlug, setNewSlug] = useState('');
  const [confirmPhrase, setConfirmPhrase] = useState('');
  const [reason, setReason] = useState('');
  const [approverEmail, setApproverEmail] = useState('');
  const [saving, setSaving] = useState(false);

  function reset() {
    setNewSlug('');
    setConfirmPhrase('');
    setReason('');
    setApproverEmail('');
  }

  async function submit() {
    if (newSlug.length < 3) {
      toast.error('New slug must be at least 3 characters');
      return;
    }
    if (confirmPhrase !== currentSlug) {
      toast.error(`Confirm phrase must equal "${currentSlug}"`);
      return;
    }
    if (reason.trim().length < 5) {
      toast.error('Reason must be at least 5 characters');
      return;
    }
    try {
      setSaving(true);
      await apiClient.post('/admin/settings/general/rename-slug', {
        newSlug,
        confirmPhrase,
        reason,
        approverEmail: approverEmail.trim() || undefined,
      });
      toast.success(`Slug renamed to "${newSlug}"`);
      reset();
      await onRenamed();
    } catch (err) {
      const msg = err instanceof Error ? err.message : 'Rename failed';
      toast.error(msg);
    } finally {
      setSaving(false);
    }
  }

  return (
    <Dialog
      open={open}
      onOpenChange={(v) => {
        if (!v) reset();
        onOpenChange(v);
      }}
    >
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>Rename tenant slug</DialogTitle>
          <DialogDescription>
            Renaming a slug requires 2-person approval and is gated by an
            active legal-hold check. The audit chain records both
            approver identities.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-3 py-2">
          <div className="space-y-1.5">
            <Label htmlFor="rename-current">Current slug</Label>
            <Input
              id="rename-current"
              value={currentSlug}
              disabled
              className="h-9 text-xs font-mono"
            />
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="rename-new">New slug</Label>
            <Input
              id="rename-new"
              value={newSlug}
              onChange={(e) => setNewSlug(e.target.value)}
              placeholder="e.g. acme-prod"
              className="h-9 text-xs font-mono"
            />
            <p className="text-[10px] text-muted-foreground">
              Lowercase letters, digits, hyphens. 3-50 characters.
            </p>
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="rename-confirm">
              Type <code className="font-mono">{currentSlug}</code> to confirm
            </Label>
            <Input
              id="rename-confirm"
              value={confirmPhrase}
              onChange={(e) => setConfirmPhrase(e.target.value)}
              className="h-9 text-xs font-mono"
            />
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="rename-approver">Second approver email</Label>
            <Input
              id="rename-approver"
              value={approverEmail}
              onChange={(e) => setApproverEmail(e.target.value)}
              placeholder="another-admin@example.com"
              className="h-9 text-xs"
            />
            <p className="text-[10px] text-muted-foreground">
              Required by the 2-person rule for tenant-write operations.
            </p>
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="rename-reason">Reason</Label>
            <Input
              id="rename-reason"
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              placeholder="e.g. Rebranding"
              className="h-9 text-xs"
            />
          </div>
        </div>

        <DialogFooter>
          <Button
            variant="ghost"
            onClick={() => onOpenChange(false)}
            disabled={saving}
          >
            Cancel
          </Button>
          <Button onClick={submit} disabled={saving}>
            {saving && <Loader2 className="h-3.5 w-3.5 animate-spin mr-1.5" />}
            Submit rename
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ─── Helpers ────────────────────────────────────────────────────────────────

function Fact({ label, value }: { label: string; value: React.ReactNode }) {
  return (
    <div>
      <div className="text-[10px] uppercase tracking-wider font-semibold text-muted-foreground mb-0.5">
        {label}
      </div>
      <div className="text-xs">{value}</div>
    </div>
  );
}

function ContactRow({
  label,
  icon: Icon,
  contact,
}: {
  label: string;
  icon: any;
  contact: { name: string; email: string; phone: string | null } | null;
}) {
  if (!contact) {
    return (
      <div className="flex items-center gap-3 rounded-md border p-2.5 bg-muted/10">
        <div className="h-8 w-8 rounded-md bg-muted/40 flex items-center justify-center shrink-0">
          <Icon className="h-4 w-4 text-muted-foreground" />
        </div>
        <div className="min-w-0 flex-1">
          <div className="text-[10px] uppercase tracking-wider font-semibold text-muted-foreground">
            {label}
          </div>
          <div className="text-xs italic text-muted-foreground">
            Not configured
          </div>
        </div>
      </div>
    );
  }
  return (
    <div className="flex items-center gap-3 rounded-md border p-2.5">
      <div className="h-8 w-8 rounded-md bg-primary/10 flex items-center justify-center shrink-0">
        <Icon className="h-4 w-4 text-primary" />
      </div>
      <div className="min-w-0 flex-1">
        <div className="text-[10px] uppercase tracking-wider font-semibold text-muted-foreground">
          {label}
        </div>
        <div className="text-xs font-medium truncate">{contact.name}</div>
        <div className="flex items-center gap-3 text-[10px] text-muted-foreground">
          <span className="inline-flex items-center gap-1">
            <Mail className="h-2.5 w-2.5" /> {contact.email}
          </span>
          {contact.phone && (
            <span className="inline-flex items-center gap-1">
              <Phone className="h-2.5 w-2.5" /> {contact.phone}
            </span>
          )}
        </div>
      </div>
    </div>
  );
}

/**
 * A mode's state, and — only for someone who may change it (`canChange`, the
 * server-reported write:settings) — its switch. Without the key the switch is
 * not in the DOM; the state reads On / Off with the reason (AC-07).
 */
function ModeToggle({
  label, description, enabled, canChange, onChange, destructive = false,
}: {
  label: string;
  description: string;
  enabled: boolean;
  canChange: boolean;
  onChange: (v: boolean) => void;
  destructive?: boolean;
}) {
  return (
    <div className="flex items-start justify-between gap-3 py-1">
      <div className="min-w-0 flex-1">
        <div className={cn(
          'text-xs font-semibold flex items-center gap-1.5',
          enabled && destructive && 'text-amber-700 dark:text-amber-400',
        )}>
          {destructive && enabled && <AlertTriangle className="h-3 w-3" />}
          {label}
          {enabled && (
            <Badge variant="outline" className={cn(
              'text-[9px]',
              destructive
                ? 'bg-amber-500/10 text-amber-700 dark:text-amber-300 border-amber-500/30'
                : 'bg-emerald-500/10 text-emerald-700 dark:text-emerald-300 border-emerald-500/30',
            )}>
              Active
            </Badge>
          )}
        </div>
        <p className="text-[11px] text-muted-foreground">{description}</p>
      </div>
      {canChange ? (
        <Switch checked={enabled} onCheckedChange={onChange} className="shrink-0 mt-1" />
      ) : (
        <ReadOnlyState on={enabled} className="shrink-0 mt-1" />
      )}
    </div>
  );
}

/** A switch's state for someone who may not flip it: On / Off and why. */
function ReadOnlyState({ on, className }: { on: boolean; className?: string }) {
  return (
    <span
      className={cn('inline-flex items-center gap-1 text-[11px] text-muted-foreground', className)}
      title={NO_WRITE_REASON}
      aria-label={`${on ? 'On' : 'Off'} — ${NO_WRITE_REASON}`}
    >
      <Badge variant="outline" className="text-[10px]">{on ? 'On' : 'Off'}</Badge>
    </span>
  );
}
